<template>
  <div class="invite-view">
    <div class="container" v-if="loadingData">
      <NiceLoading :message="$gettext('Fetching data...')" fullDiv class="p-5" />
    </div>

    <div class="container" v-if="data">
      <!-- Header -->
      <section class="invite-view-header">
        <img :src="data.event.cover_photo" />
        <h1>{{ data.event.name }}</h1>
        <p :title="$gettext('Event dates')">
          <NiceIcon icon="icon-calendar" />
          <!-- <span>{{ $gettext("Start date") }}: </span> -->
          <span>{{ formatDateWithTime(data.event.start_time, false, true) }}</span>
          <span> - </span>
          <!-- <NiceIcon icon="icon-calendar" /> -->
          <!-- <span>{{ $gettext("End date") }}: </span> -->
          <span>{{ formatDateWithTime(data.event.end_time, false, true) }}</span>
        </p>
        <p :title="$gettext('Invite link expires')">
          <NiceIcon icon="icon-alert-circle" />
          <span>{{ $gettext("Invite link expires") }}: </span>
          <span>{{ formatDateWithTime(data.expire_at, false, true) }}</span>
        </p>
        <p :title="$gettext('Partner name')">
          <NiceIcon icon="icon-user" />
          <span>{{ data.name }}</span>
        </p>
        <div class="invite-view-intro mt-4" v-if="data.intro_content">{{ data.intro_content }}</div>
      </section>

      <hr />

      <!-- Body -->
      <div class="invite-view-body">
        <!-- Expired alert -->
        <section class="alert alert-warning mb-3" v-if="linkExpired">
          <NiceIcon class="alert-icon" icon="icon-alert-triangle"></NiceIcon>
          <div class="alert-info">
            <h1 class="alert-title">{{ $gettext("Expired") }}</h1>
            <p class="alert-message">{{ $gettext("Invite link has expired") }}</p>
          </div>
        </section>

        <!-- No guests -->
        <section class="alert alert-warning mb-3" v-if="!success && guestLimitReached">
          <NiceIcon class="alert-icon" icon="icon-alert-triangle"></NiceIcon>
          <div class="alert-info">
            <h1 class="alert-title">{{ $gettext("Guest limit reached") }}</h1>
            <p class="alert-message">{{ $gettext("You have used up all guest invites you were given") }}</p>
          </div>
        </section>

        <!-- Confirm -->
        <section class="alert alert-success" v-if="success">
          <NiceIcon class="alert-icon" icon="icon-check"></NiceIcon>
          <div class="alert-info">
            <h1 class="alert-title">{{ $gettext("Success") }}</h1>
            <p class="alert-message">
              {{ data.submit_confirmation_content || $gettext("Guests were successfully submitted. You can add more by closing this notification.") }}
            </p>
            <div class="d-flex mt-3">
              <!-- Close -->
              <NiceButton @click="closeSuccess" type="success" icon="icon-x" :text="$gettext('Close')"></NiceButton>

              <!-- Download tickets PDF -->
              <NiceButton
                v-if="data.allow_pdf_download"
                @click="generateTicketsPDF"
                type="success"
                plain
                icon="icon-download"
                :text="$gettext('Download tickets PDF')"
                :loading="loading"
              ></NiceButton>
            </div>
          </div>
        </section>

        <!-- New guests -->
        <section v-if="!success && !guestLimitReached && !linkExpired">
          <h5 class="title">{{ $gettext("New guests") }} - {{ guests.length }}</h5>
          <form @submit.prevent="submit">
            <!-- New guests -->
            <div class="new-guests">
              <NiceWrapper v-for="(guest, key) in guests" :key="key" noMargin>
                <template #header v-if="false">
                  <div class="invite-guest-header">
                    <div class="guest-avatar">
                      <img v-if="guest.photo" :src="guest.photo" />
                      <NiceIcon v-else icon="icon-user"></NiceIcon>
                    </div>
                    <span>{{ (guest.first_name || "-") + " " + (guest.last_name || "-") }}</span>
                  </div>
                </template>

                <!-- First name -->
                <NiceInput :title="$gettext('First name')" v-model="guest.first_name" type="text" :required="true" :placeholder="$gettext('John')" />

                <!-- Last name -->
                <NiceInput :title="$gettext('Last time')" v-model="guest.last_name" type="text" :required="true" :placeholder="$gettext('Doe')" />

                <!-- Email -->
                <NiceInput
                  :title="$gettext('Email')"
                  v-model="guest.email"
                  type="email"
                  :required="data.guest_group.require_email"
                  v-if="!(!data.show_non_required_fields && !data.guest_group.require_email)"
                  :placeholder="$gettext('john@doe.com')"
                />

                <!-- Phone number -->
                <NiceInput
                  :title="$gettext('Phone number')"
                  v-model="guest.phone_number"
                  type="text"
                  placeholder="000 000 000"
                  :required="data.guest_group.require_phone_number"
                  v-if="!(!data.show_non_required_fields && !data.guest_group.require_phone_number)"
                />

                <!-- Company name -->
                <NiceInput
                  :title="$gettext('Company name')"
                  v-model="guest.company_name"
                  type="text"
                  placeholder="Olaii d.o.o."
                  :required="data.guest_group.require_company"
                  v-if="!(!data.show_non_required_fields && !data.guest_group.require_company)"
                />

                <!-- Plus one count -->
                <NiceInput
                  v-if="data.guest_group.allow_plus_one"
                  :title="$gettext('Plus one count')"
                  v-model="guest.plus_one_count"
                  :max="data.guest_group.plus_one_limit_per_guest"
                  :min="0"
                  type="number"
                  placeholder="0"
                />

                <!-- Photo -->
                <NiceUpload
                  :title="$gettext('Photo')"
                  v-model="guest.photo"
                  accept="image/png, image/jpeg"
                  @change="onFileChange($event, guest)"
                  :required="data.guest_group.require_photo"
                  v-if="!(!data.show_non_required_fields && !data.guest_group.require_photo)"
                  :loading="guest.loading"
                />

                <!-- Remove -->
                <NiceButton
                  icon="icon-trash-2"
                  type="danger"
                  size="small"
                  plain
                  :text="$gettext('Remove')"
                  @click="removeGuest(guest, key)"
                ></NiceButton>

                <!-- Footer -->
                <!-- <template #footer>
                <div class="d-flex">
                  <div class="grow"></div>
                  <NiceButton icon="icon-save" :text="$gettext('Submit')"></NiceButton>
                </div>
              </template> -->

                <!-- <pre>{{ guest }}</pre> -->
              </NiceWrapper>

              <!-- Add -->
              <button type="button" class="btn btn-add" @click="addGuest" :disabled="data.available_count - guests.length <= 0">
                <NiceIcon icon="icon-plus" />
                <div>{{ $gettext("Add new guest") }}</div>
                <div v-if="data.show_guest_limit">({{ data.guest_count + guests.length }} / {{ data.guest_limit }})</div>
              </button>
            </div>

            <!-- Actions -->
            <div class="d-flex">
              <div class="grow"></div>
              <NiceButton
                icon="icon-save"
                nativeType="submit"
                :loading="loading"
                :disabled="loading || guests.length == 0"
                :text="$gettext('Submit')"
              ></NiceButton>
            </div>
          </form>
        </section>

        <!-- Guests -->
        <section v-if="data.show_guests">
          <hr />

          <h5 class="title">{{ $gettext("Guests") }} - {{ data.guests.length }}</h5>

          <!-- Download tickets PDF -->
          <NiceButton
            v-if="data.allow_pdf_download && data.guests.length > 0"
            @click="generateTicketsPDF"
            type="primary"
            icon="icon-download"
            :text="$gettext('Download tickets PDF')"
            :loading="loading"
            class="mb-2"
          ></NiceButton>

          <NiceNoData v-if="data.guests.length == 0" class="nice-no-data-box box"></NiceNoData>

          <div class="guests">
            <div class="guest box" v-for="(guest, key) in data.guests" :class="guest.status" :key="key">
              <div class="guest-avatar">
                <img v-if="guest.photo" :src="guest.photo" />
                <NiceIcon v-else icon="icon-user"></NiceIcon>
              </div>
              <div class="guest-info">
                <b>{{ (guest.first_name || "-") + " " + (guest.last_name || "-") }}</b>
                <div class="text-small">Email: {{ guest.email || "-" }}</div>
                <div class="text-small">Phone number: {{ guest.phone_number || "-" }}</div>
                <div class="text-small">Company: {{ guest.company_name || "-" }}</div>
                <div class="badge badge-sm" :class="CommonService.getGuestStatus(guest.status).class">
                  {{ CommonService.getGuestStatus(guest.status).name }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Data -->
        <!-- <NiceWrapper title="Data" class="mt-4" collapsable collapsed>
          <pre>{{ data }}</pre>
        </NiceWrapper> -->
      </div>

      <!-- Footer -->
      <hr />
      <div class="invite-view-footer">
        <div class="invite-view-footer-left">
          <a target="_blank" href="https://olaii.com">
            <NiceIcon icon="logo" class="logo" />
          </a>
        </div>

        <div class="invite-view-footer-middle">
          <LanguageSwitcher />
          <div>
            <span>© 2014-{{ new Date().getFullYear() }}&nbsp;</span>
            <span>Olaii d.o.o., Vse pravice pridržane.</span>
          </div>
        </div>

        <div class="invite-view-footer-right">
          <ul>
            <li><a target="_blank" href="https://olaii.com/b2b/prodaja-vstopnic/">Prodaja vstopnic</a></li>
            <li><a target="_blank" href="https://olaii.com/b2b/davcna-blagajna/">Davčna blagajna</a></li>
            <!-- <li><a target="_blank" href="https://olaii.com/b2b/cashless-placevanje/">Cashless</a></li> -->
            <li><a target="_blank" href="https://olaii.com/b2b/kartomat/">Kartomat</a></li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { formatDateWithTime } from "@/plugins/date";
import { isAfter } from "date-fns";

import ApiService from "@/services/api/api.service";
import ErrorService from "@/services/error.service";
import CommonService from "@/services/common.service";
import StorageService from "@/services/storage.service";

import LanguageSwitcher from "@/components/LanguageSwitcher";

import gettext from "@/language";
const { $gettext } = gettext;

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();

const inviteLinkUUID = route.params.uuid;
const loading = ref(false);
const loadingData = ref(false);
const success = ref(false);
const data = ref(null);
const guests = ref([]);
const guestLimitReached = computed(() => (data.value ? data.value.available_count <= 0 : false));
const linkExpired = computed(() => (data.value ? isAfter(new Date(), data.value.expire_at) : false));

function removeGuest(guest, index) {
  guests.value.splice(index, 1);
}

function closeSuccess() {
  success.value = false;
  getData();
}

function addGuest() {
  const newGuest = {
    first_name: null,
    last_name: null,
    company_name: null,
    email: null,
    phone_number: null,
    photo: null,
    plus_one_count: 0,
  };
  guests.value.push(newGuest);
}

async function getData() {
  loadingData.value = true;
  try {
    data.value = await ApiService.invites.get(inviteLinkUUID);

    // if (data.value.show_guests) guests.value = data.value.guests;
    guests.value = [];

    // Add default guest
    if (data.value.available_count > 0) {
      addGuest();
    }

    // Set language
    if (data.value.default_language && !StorageService.get("olaii_guestlist_language")) {
      generalStore.setLanguage(data.value.default_language);
    }
  } catch (error) {
    ErrorService.showError(error);
    const err = ErrorService.getError(error);
    router.push({ name: "error", query: err });
  } finally {
    loadingData.value = false;
  }
}

async function submit() {
  loading.value = true;
  try {
    await ApiService.invites.submitGuests(inviteLinkUUID, guests.value);
    getData();
    success.value = true;
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function generateTicketsPDF() {
  loading.value = true;
  try {
    const response = await ApiService.invites.generateTicketsPDF(inviteLinkUUID);
    CommonService.downloadBlob(response, "tickets.pdf");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function onFileChange(file, data) {
  if (!file) return (data.photo = null);
  if (!file[0]) return (data.photo = null);
  if (!["image/jpeg", "image/png"].includes(file[0].type)) {
    CommonService.error($gettext("Wrong file format. Only .jpg and .png files are allowed."));
    return (data.photo = null);
  }

  data.loading = true;
  try {
    const response = await ApiService.storage.uploadPhoto(file[0], CommonService.photoCompressionTypes.COVER, null, inviteLinkUUID);
    data.photo = response.file;
  } catch (error) {
    ErrorService.showError(error);
    data.photo = null;
  } finally {
    data.loading = false;
  }
}

onMounted(() => {
  getData();
});
</script>

<style lang="scss" scoped>
.invite-view {
  background-color: var(--background-color);

  .container {
    max-width: 100%;
    width: 1200px;
    margin: 0 auto;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    // gap: 2rem;
  }

  .alert {
    color: var(--font-color);
    border-radius: var(--border-radius);
    padding: 2rem;
    border: 1px solid var(--border-color);
    background: var(--nice-card-bg);

    display: flex;
    gap: 2rem;

    @media (max-width: 700px) {
      flex-direction: column;
    }

    .nice-icon {
      flex-shrink: 0;
      width: 80px;
      height: 80px;
    }

    .alert-title {
      font-size: 2em;
      font-weight: bold;
      margin-bottom: 0.2rem;
    }

    .alert-message {
      margin: 0;
      padding: 0;
      margin-top: 0.5rem;
      white-space: pre-wrap;
    }

    &.alert-success {
      border: 1px solid var(--success-color);
      background: var(--nice-success-color-lighter);

      .nice-icon {
        border: 4px solid var(--success-color-darker);
        fill: var(--success-color-darker);
        border-radius: 50%;
        padding: 1rem;
        width: 100px;
        height: 100px;
      }

      .alert-title {
        color: var(--nice-success-color-darker);
      }
    }

    &.alert-danger {
      border: 1px solid var(--danger-color);
      background: var(--nice-danger-color-lighter);
      color: var(--nice-danger);

      .nice-icon {
        fill: var(--danger-color-darker);
      }

      .alert-title {
        color: var(--nice-danger-color-darker);
      }
    }

    &.alert-warning {
      border: 1px solid var(--warning-color);
      background: var(--nice-warning-color-lighter);
      color: var(--nice-warning);

      .nice-icon {
        fill: var(--warning-color-darker);
      }

      .alert-title {
        color: var(--nice-warning-color-darker);
      }
    }
  }

  .guest-avatar {
    position: relative;
    flex-shrink: 0;
    height: 65px;
    width: 65px;
    border-radius: 50%;
    background: var(--border-color);
    overflow: hidden;
    color: #eee;

    .nice-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      height: 60%;
      width: 60%;
      transform: translate(-50%, -50%);
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }

  .invite-view-header {
    flex-shrink: 0;

    img {
      width: 100%;
      max-height: 500px;
      object-fit: cover;
      border-radius: var(--border-radius);
      margin-bottom: 1rem;
    }

    h1 {
      color: var(--primary-color);
      margin: 0;
      padding: 0;
      font-size: 2.5em;
      font-weight: bold;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      padding: 0;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.3rem;

      .nice-icon {
        height: 14px;
        width: 14px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    .invite-view-intro {
      white-space: pre-wrap;
    }
  }

  .invite-view-body {
    flex-grow: 1;

    .new-guests {
      display: grid;
      gap: 1rem;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      grid-auto-rows: 1fr;
      margin-bottom: 2rem;
    }

    .invite-guest-header {
      display: flex;
      align-items: center;
      gap: 1rem;

      .guest-avatar {
        height: 50px;
        width: 50px;
      }

      span {
        font-weight: bold;
        font-size: 1.1em;
      }
    }

    .guests {
      display: grid;
      gap: 1rem;
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));

      .guest {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.5rem;

        &.DEACTIVATED {
          opacity: 0.5;
          b {
            text-decoration: line-through;
          }
        }

        .guest-info {
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0px;

          .badge {
            margin-top: 4px;
          }
        }
      }
    }

    .btn-add {
      background: transparent;
      border-style: dashed;
      border-width: 3px;
      display: flex;
      flex-direction: column;
      align-content: center;
      justify-content: center;
      color: var(--primary-color);
      width: 100%;
      padding: 2rem;
      height: auto;

      .nice-icon {
        height: 40px;
        width: 40px;
        flex-shrink: 0;
      }
    }
  }

  .invite-view-footer {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    font-size: 0.8em;

    .invite-view-footer-left {
      text-align: left;
    }

    .invite-view-footer-middle {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .invite-view-footer-right {
      text-align: right;

      ul {
        list-style: none;
      }
    }

    @media (max-width: 576px) {
      grid-template-columns: 1fr 1fr;

      .invite-view-footer-middle {
        text-align: right;
        flex-grow: 1;
        align-items: flex-end;
      }

      .invite-view-footer-right {
        display: none;
      }
    }

    .logo {
      height: 50px;
      width: 74px;
      color: var(--primary-color);
    }
  }
}
</style>
